import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AdminRole = "admin" | "editor";

export interface AdminAuthState {
  loading: boolean;
  session: Session | null;
  user: User | null;
  roles: AdminRole[];
  isAuthorized: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

export function useAdminAuth(): AdminAuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Listener FIRST, then getSession (per Supabase guidance)
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      if (newSession?.user) {
        // defer role fetch to avoid deadlocks inside the callback
        setTimeout(() => fetchRoles(newSession.user.id), 0);
      } else {
        setRoles([]);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      if (data.session?.user) {
        fetchRoles(data.session.user.id);
      } else {
        setLoading(false);
      }
    });

    async function fetchRoles(userId: string) {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);
      if (!mounted) return;
      if (error) {
        console.error("Failed to load roles", error);
        setRoles([]);
      } else {
        setRoles((data ?? []).map((r) => r.role as AdminRole));
      }
      setLoading(false);
    }

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const isAdmin = roles.includes("admin");
  const isAuthorized = isAdmin || roles.includes("editor");

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return {
    loading,
    session,
    user: session?.user ?? null,
    roles,
    isAuthorized,
    isAdmin,
    signOut,
  };
}
